import * as Sentry from '@sentry/browser';
import {
  Pagination,
  Table,
  useTableHelpers,
  useTablePagination,
} from '@teamsnap/snap-ui';
import ExportModal from 'components/common/export-modal';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import { createFilterID } from 'core/utils';
import {
  AMPLITUDE_FINANCIAL_ACTIONS,
  AMPLITUDE_REPORT_TYPE,
  useAmplitude,
} from 'hooks/amplitude';
import {
  LoadingType,
  useFinancials,
  useFinancialsActions,
} from 'hooks/financials';
import { camelCase, isEmpty } from 'lodash';
import * as React from 'react';
import { useEffect } from 'react';
import {
  buildCSVFileExport,
  columnsForExportModal,
} from '../financials-exports';
import FinancialsTableSkeletonLoader from '../financials-table-skeleton-loader';
import {
  applySearchTermToItems,
  beginningOfDay,
  endOfDay,
  formatProgramNames,
  joinNamesByComma,
  orgDateTimeFromUTCDate,
  sourceNameForSourceItems,
} from '../financials-utils';
import { useAmplitudeFilterTracking } from '../hook';
import {
  DATE_RANGE_ACTIONS_OPTIONS_FOR_AMPLITUDE,
  PAYMENT_TYPES,
} from '../types';
import { getColumns } from './columns';
import { TransactionFilters } from './transaction-filters';
import TransactionSummary from './transaction-summary';

export default function FinancialsTransactionsList() {
  const [showExportModal, setShowExportModal] = React.useState<boolean>(false);
  const {
    prepareFilterForAmplitude,
    trackResultsUsingLastFilterForAmplitude,
    trackOnClearForAmplitude,
  } = useAmplitudeFilterTracking(AMPLITUDE_REPORT_TYPE.TRANSACTIONS);
  const {
    organization,
    transactions,
    loading,
    transactionFilters: filters,
  } = useFinancials();
  const { getTransactions, updateTransactionsFilter } = useFinancialsActions();
  const { amplitudeTrack } = useAmplitude();

  useEffect(() => {
    if (!organization) return;
    const dateColumn =
      filters.dateType === 'payment' ? 'transaction_date' : 'deposit_date';
    const startDate = orgDateTimeFromUTCDate(
      organization?.timezoneIana,
      beginningOfDay(filters.datesRange[0])
    );
    const endDate = orgDateTimeFromUTCDate(
      organization?.timezoneIana,
      endOfDay(filters.datesRange[1])
    );
    getTransactions(
      Number(organization.id),
      dateColumn,
      startDate,
      endDate
    ).catch((e) => {
      Sentry.captureException(e);
      // TODO: Add notification for user if this fails
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, filters.dateType, filters.datesRange]);

  const onActionClick = (action: AMPLITUDE_FINANCIAL_ACTIONS) => {
    amplitudeTrack.tsbFinancialActionActionClicked({
      financial_action: action,
      financial_report: AMPLITUDE_REPORT_TYPE.TRANSACTIONS,
    });
  };

  const columns = getColumns(onActionClick, organization?.timezoneIana);

  const setFilter = (key, value) => {
    updateTransactionsFilter(key, value);
    prepareFilterForAmplitude(key);
    onPageChange(1);
  };

  const filterAdditionalACH = (filters, transaction) =>
    filters.paymentType.includes(PAYMENT_TYPES.US_BANK_ACCOUNT) &&
    transaction.paymentType === PAYMENT_TYPES.ACH;

  const filterAdditionalCC = (filters, transaction) =>
    filters.paymentType.includes(PAYMENT_TYPES.CARD) &&
    transaction.paymentType === PAYMENT_TYPES.CREDIT_CARD;

  const filterOther = (filters, transaction) =>
    filters.paymentType.includes(PAYMENT_TYPES.OTHER) &&
    !Object.values(PAYMENT_TYPES).includes(
      transaction.paymentType as PAYMENT_TYPES
    );

  const handleOnCaptureDateRangeAction = (
    id: keyof typeof DATE_RANGE_ACTIONS_OPTIONS_FOR_AMPLITUDE
  ) => {
    amplitudeTrack.tsbFinancialDateRangeChanged({
      financial_date_option: DATE_RANGE_ACTIONS_OPTIONS_FOR_AMPLITUDE[id],
      financial_report: AMPLITUDE_REPORT_TYPE.TRANSACTIONS,
    });
  };

  // NOTE: Main useMemo to parse and filter data
  const parsedData = React.useMemo(() => {
    if (!transactions.length) return [];
    let dataToReturn = transactions;

    dataToReturn = dataToReturn
      .filter(
        (transaction) =>
          filters.program.length === 0 ||
          transaction.sourceItems.filter((sourceItem) =>
            filters.program.includes(createFilterID(sourceItem.programSeason))
          ).length > 0
      )
      .filter(
        (transaction) =>
          filters.sourceType.length === 0 ||
          filters.sourceType.includes(camelCase(transaction.orderType))
      )
      // NOTE: Special case for registration and invoice filters
      .filter((transaction) =>
        filters.registration.length > 0 || filters.invoice.length > 0
          ? transaction.sourceItems[0]?.programSeason
            ? (filters.registration.includes(
                createFilterID(transaction.sourceItems[0]?.programSeason)
              ) &&
                camelCase(transaction.orderType) === 'registration') ||
              (filters.invoice.includes(
                createFilterID(transaction.sourceItems[0]?.programSeason)
              ) &&
                camelCase(transaction.orderType) === 'invoice')
            : filters.registration.includes(null)
          : true
      )
      .filter(
        (transaction) =>
          isEmpty(filters.status) || filters.status.includes(transaction.status)
      )
      .filter(
        (transaction) =>
          filters.depositAccount.length === 0 ||
          (transaction.merchantAccountDisplayName
            ? filters.depositAccount.includes(
                createFilterID(transaction.merchantAccountDisplayName)
              )
            : filters.depositAccount.includes(null)) // this corresponds to No deposit acount filter
      )
      .filter(
        (transaction) =>
          filters.paymentType.length === 0 ||
          filters.paymentType.includes(transaction.paymentType) ||
          filterAdditionalACH(filters, transaction) ||
          filterAdditionalCC(filters, transaction) ||
          filterOther(filters, transaction)
      );

    if (filters.searchTerm) {
      dataToReturn = applySearchTermToItems(dataToReturn, filters.searchTerm);
    }

    return dataToReturn.map((row) => ({
      ...row,
      // NOTE: Flat important keys for the table
      transactionDate: new Date(
        orgDateTimeFromUTCDate(
          organization?.timezoneIana,
          new Date(row.transactionDate)
        )
      ),
      programName: formatProgramNames(row.sourceItems),
      member: joinNamesByComma(row.sourceItems),
      source: sourceNameForSourceItems(row.sourceItems),
      depositAccount: row.merchantAccountDisplayName,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, filters]);

  const { rows, headerRow } = useTableHelpers({
    data: parsedData,
    columns: { ...columns },
    initialSorting: {
      method: 'date',
      sortKey: 'transactionDate',
      acsending: false,
    },
  });

  const { currentPage, onPageChange, totalCount, currentRows, pageSize } =
    useTablePagination({
      rows,
      // NOTE: Set pageSize to 30 because there is a bug wiht SnapUI Pagination hook and large data sets
      pageSize: 30,
    });

  trackResultsUsingLastFilterForAmplitude(rows.length);

  const exportModalColumns = columnsForExportModal(columns);

  const exportTypes = [
    { label: 'Transactions', value: 'financial_transactions_export' },
  ];

  const exportModalCallback = (
    exportColumns,
    exportType,
    templateName = ''
  ) => {
    try {
      buildCSVFileExport(parsedData, exportColumns, exportType, templateName);
      setShowExportModal(false);
      amplitudeTrack.tsbFinancialsExported(AMPLITUDE_REPORT_TYPE.TRANSACTIONS);
    } catch (error) {
      reactiveFeedbackMessage({
        message:
          'Oops! Export could not be created. Please try again or contact TeamSnap support for help.',
        isError: true,
        autoclose: true,
      });
    }
  };

  return (
    <div className={'sui-py-4'} data-testid="transaction-tab">
      <TransactionSummary />
      <TransactionFilters
        setFilter={setFilter}
        trackOnClearForAmplitude={trackOnClearForAmplitude}
        handleOnCaptureDateRangeAction={handleOnCaptureDateRangeAction}
        displayExportModal={() => setShowExportModal(true)}
      />

      {loading[LoadingType.GetTransactions] ? (
        <FinancialsTableSkeletonLoader />
      ) : (
        <>
          <div className="sui-overflow-x-auto sui-mb-2">
            <div
              style={{ width: 'max-content', minWidth: '100%' }}
              data-testid="transaction-list__table"
            >
              <Table headerRow={headerRow} rows={currentRows} />
            </div>
          </div>
          <ExportModal
            columns={exportModalColumns}
            title="Generate Transactions Export"
            show={showExportModal}
            organizationId={Number(organization?.id)}
            exportTypes={exportTypes}
            onClose={() => setShowExportModal(false)}
            onSubmit={exportModalCallback}
          />
          <Pagination
            {...{ currentPage, onPageChange, totalCount, pageSize }}
          />
        </>
      )}
    </div>
  );
}
